import {
  Chart,
  ChartOptions,
  Filler,
  LineElement,
  Plugin,
  PointElement,
  RadarController,
  RadialLinearScale,
  Tooltip,
} from 'chart.js';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { MAX_DIMENSION_SCORE, RADAR_BAND_COLOR } from '../AssessmentIndex.constants';
import { colors } from '../../../../../globalStyles';
import { formatDisplayValue } from '../utils';

Chart.register(RadarController, RadialLinearScale, PointElement, LineElement, Filler, Tooltip);

type RadarChartProps = {
  circular?: boolean;
  color: string;
  fill: string;
  labels: string[];
  values: number[];
};

type RadialScaleInternals = RadialLinearScale & {
  xCenter: number;
  yCenter: number;
};

const createRadarBandsPlugin = (circular: boolean): Plugin<'radar'> => ({
  id: `assessment-index-radar-bands-${circular ? 'circular' : 'polygonal'}`,
  beforeDraw: chart => {
    const scale = chart.scales.r as RadialLinearScale;
    const pointCount = chart.data.labels?.length ?? 0;

    if (pointCount < 3) return;

    const { ctx } = chart;
    ctx.save();

    [10, 8, 6, 4, 2].forEach((value, index) => {
      ctx.beginPath();

      if (circular) {
        const circularScale = scale as RadialScaleInternals;
        ctx.arc(
          circularScale.xCenter,
          circularScale.yCenter,
          scale.getDistanceFromCenterForValue(value),
          0,
          Math.PI * 2
        );
      } else {
        for (let pointIndex = 0; pointIndex < pointCount; pointIndex += 1) {
          const point = scale.getPointPositionForValue(pointIndex, value);
          if (pointIndex === 0) {
            ctx.moveTo(point.x, point.y);
          } else {
            ctx.lineTo(point.x, point.y);
          }
        }

        ctx.closePath();
      }

      ctx.fillStyle = index % 2 === 0 ? RADAR_BAND_COLOR : colors.white;
      ctx.fill();
    });

    ctx.restore();
  },
});

const radarOptions = (
  color: string,
  circular: boolean,
  pointCount: number
): ChartOptions<'radar'> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 400 },
  layout: {
    padding: {
      top: 14,
      right: pointCount > 4 ? 30 : 52,
      bottom: pointCount > 4 ? 36 : 36,
      left: pointCount > 4 ? 30 : 52,
    },
  },
  scales: {
    r: {
      min: 0,
      max: MAX_DIMENSION_SCORE,
      ticks: {
        display: false,
        stepSize: MAX_DIMENSION_SCORE / 5,
      },
      pointLabels: {
        color: colors.text,
        font: { size: 12, weight: 'bold' },
      },
      grid: { circular, color: '#CED1D5', lineWidth: 1 },
      angleLines: { color: '#CED1D5', lineWidth: 1 },
    },
  },
  elements: {
    line: {
      borderColor: color,
      borderWidth: 2,
    },
    point: {
      backgroundColor: color,
      radius: 3,
      hoverRadius: 5,
    },
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: ({ label, raw }) =>
          `${label}: ${formatDisplayValue(Number(raw))} / ${MAX_DIMENSION_SCORE}`,
      },
    },
  },
});

export const RadarChart = ({ circular = false, color, fill, labels, values }: RadarChartProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart<'radar'>>();
  const labelsKey = labels.join(',');
  const valuesKey = values.join(',');

  useEffect(() => {
    if (!canvasRef.current) return;

    chartRef.current = new Chart(canvasRef.current, {
      type: 'radar',
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: fill,
            borderColor: color,
            pointBackgroundColor: color,
          },
        ],
      },
      options: radarOptions(color, circular, labels.length),
      plugins: [createRadarBandsPlugin(circular)],
    });

    return () => chartRef.current?.destroy();
    // The joined keys recreate the chart only when its displayed data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [circular, color, fill, labelsKey, valuesKey]);

  return <RadarCanvas ref={canvasRef} />;
};

const RadarCanvas = styled.canvas`
  width: 100% !important;
  height: 100% !important;
`;
